import { Link } from 'react-router-dom';

function SupportHint({ dark }) {
  const email = (process.env.REACT_APP_SUPPORT_EMAIL || '').trim();
  const linkStyle = { color: dark ? '#fde047' : '#b45309', fontWeight: 600 };
  if (email) {
    return (
      <>
        {' '}
        Contact{' '}
        <a href={`mailto:${email}`} style={linkStyle}>
          {email}
        </a>{' '}
        with your challenge number.
      </>
    );
  }
  return (
    <>
      {' '}
      Use the contact or help options listed on the site, and include your <strong>challenge number</strong>.
    </>
  );
}

/**
 * Short copy above “I won” / “I lost” so players use the fair order and know what to do if someone cheats.
 */
export function ConfirmWinnerFairPlayTips({ variant = 'dark' }) {
  const dark = variant === 'dark';
  return (
    <div
      style={{
        marginBottom: 14,
        padding: '12px 14px',
        borderRadius: 8,
        border: dark ? '1px solid rgba(52, 211, 153, 0.35)' : '1px solid #a7f3d0',
        background: dark ? 'rgba(6, 78, 59, 0.35)' : '#ecfdf5',
        color: dark ? '#d1fae5' : '#064e3b',
        fontSize: '0.88rem',
        lineHeight: 1.55,
      }}
      role="region"
      aria-label="How to confirm the winner fairly"
    >
      <p style={{ margin: '0 0 8px 0', fontWeight: 700 }}>Confirm in this order (fairest)</p>
      <ol style={{ margin: 0, paddingLeft: 18 }}>
        <li style={{ marginBottom: 6 }}>
          <strong>Winner</strong> taps <strong>“I won – pay me”</strong> first.
        </li>
        <li style={{ marginBottom: 6 }}>
          <strong>Loser</strong> then taps <strong>“I lost – pay opponent”</strong>.
        </li>
      </ol>
      <p style={{ margin: '10px 0 0 0', fontSize: '0.85rem', opacity: 0.95 }}>
        The first tap locks which wallet may receive the pot. If the wrong person went first,{' '}
        <strong>do not</strong> confirm a false result—<SupportHint dark={dark} />
      </p>
    </div>
  );
}

/**
 * Plain steps so players know exactly how the right person gets paid.
 * Covers automatic paths (where applicable) and mandatory manual confirmation rules.
 */
export default function WinnerPayoutGuide({
  gameId,
  variant = 'light',
  mode = 'create',
  autoVerify = true,
  compact = false,
  /** @type {'api_when_possible' | 'player_confirm_only'} */
  settlementPreference = 'api_when_possible',
}) {
  const id = Number(gameId);
  const dark = variant === 'dark';
  const confirmOnly = settlementPreference === 'player_confirm_only';

  const box = {
    marginTop: compact ? 0 : mode === 'create' ? '1rem' : 0,
    marginBottom: compact ? 0 : '0.5rem',
    padding: compact ? '12px 14px' : '16px 18px',
    borderRadius: 10,
    fontSize: compact ? '0.86rem' : '0.9rem',
    lineHeight: 1.55,
    border: dark ? '1px solid rgba(148, 163, 184, 0.35)' : '1px solid #cbd5e1',
    background: dark ? 'rgba(15, 23, 42, 0.5)' : '#f8fafc',
    color: dark ? '#e2e8f0' : '#1e293b',
  };

  const hStyle = {
    margin: compact ? '0 0 8px 0' : '0 0 10px 0',
    fontSize: compact ? '0.92rem' : '1rem',
    fontWeight: 700,
    color: dark ? '#f1f5f9' : '#0f172a',
  };

  const olStyle = { margin: '0 0 12px 0', paddingLeft: 20, color: dark ? '#cbd5e1' : '#334155' };
  const liStyle = { marginBottom: 6 };
  const warnBox = {
    marginTop: 12,
    padding: '12px 14px',
    borderRadius: 8,
    border: dark ? '1px solid rgba(248, 113, 113, 0.45)' : '1px solid #fecaca',
    background: dark ? 'rgba(127, 29, 29, 0.25)' : '#fef2f2',
    color: dark ? '#fecaca' : '#7f1d1d',
    fontSize: '0.88rem',
    lineHeight: 1.5,
  };

  const autoIntro =
    mode === 'create'
      ? 'If you add and verify player IDs, the app can try to read the result from game data after the match.'
      : 'Because your IDs are verified, the app will try to detect the result automatically after you report the match.';

  const manualTitle = 'Always works: both players confirm the winner';

  const manualCore = (
    <>
      <p style={{ margin: '0 0 8px 0', color: dark ? '#94a3b8' : '#475569' }}>
        This path does <strong>not</strong> need FACEIT, Ballchasing, or any extra site—only the two deposit
        wallets. Use it if automation fails or you did not use a supported setup.
      </p>
      <ol style={{ ...olStyle, marginBottom: 8 }}>
        <li style={liStyle}>
          <strong>Fair order (important):</strong> The player who <strong>actually won</strong> should go first
          and tap <strong>“I won – pay me”</strong>.
        </li>
        <li style={liStyle}>
          The player who <strong>lost</strong> should then tap <strong>“I lost – pay opponent”</strong> (that
          confirms the same winner wallet).
        </li>
        <li style={liStyle}>
          The <strong>first</strong> confirmation locks <strong>which wallet</strong> may receive the pot. The
          second person must confirm that <strong>same</strong> wallet—so the real winner should confirm first
          whenever possible.
        </li>
      </ol>
      <div style={warnBox} role="alert">
        <strong>If someone tapped the wrong button first</strong> (for example the loser tapped “I won”):{' '}
        <strong>do not</strong> confirm a result you know is false—that would approve paying the wrong person.
        Funds stay in escrow until the truth is sorted out.
        <SupportHint dark={dark} />
      </div>
      <p style={{ margin: '10px 0 0 0', fontSize: '0.85rem', color: dark ? '#64748b' : '#64748b' }}>
        Refusing to tap “I lost” after a fair loss only delays payout; it does not return your stake. Be honest so
        the right winner is paid and you keep a good reputation with opponents.
      </p>
      <p style={{ margin: '10px 0 0 0', fontSize: '0.85rem', color: dark ? '#94a3b8' : '#475569' }}>
        On the challenge page, either player can use <strong>Reset confirmations</strong> if only one person
        confirmed, or if both confirmed but the payout transaction failed—so you are not permanently stuck.
      </p>
    </>
  );

  let autoSteps = null;
  if (id === 1) {
    autoSteps = (
      <ol style={olStyle}>
        <li style={liStyle}>
          <strong>For automatic payout:</strong> both players need <strong>FACEIT</strong> accounts, linked{' '}
          <strong>FACEIT IDs</strong> here, and you must play this challenge on <strong>FACEIT</strong> (not
          ordinary Steam matchmaking).
        </li>
        <li style={liStyle}>
          After the map/match ends, one player taps <strong>“We&apos;ve played – find winner”</strong> as soon as
          you can so match data is still easy to fetch.
        </li>
        <li style={liStyle}>
          If nothing happens after a while, use <strong>manual confirmation</strong> below—Steam-only games always
          need that path today.
        </li>
      </ol>
    );
  } else if (id === 2) {
    autoSteps = (
      <ol style={olStyle}>
        <li style={liStyle}>
          Both players verify their <strong>Steam IDs</strong>. Play a Dota match where you are both in the{' '}
          <strong>same Valve match</strong> (opposite teams for a normal win/loss).
        </li>
        <li style={liStyle}>
          Right after the game, one player taps <strong>“We&apos;ve played – find winner”</strong>. Do this
          within about <strong>an hour</strong> if you can—older matches are harder for the system to match.
        </li>
        <li style={liStyle}>
          If auto lookup fails, use <strong>manual confirmation</strong> below so the real winner still gets paid.
        </li>
      </ol>
    );
  } else if (id === 3) {
    autoSteps = (
      <ol style={olStyle}>
        <li style={liStyle}>
          Both verify <strong>Riot ID</strong> and the same <strong>routing region</strong>. Play your agreed
          match in League.
        </li>
        <li style={liStyle}>
          Soon after the game, one player taps <strong>“We&apos;ve played – find winner”</strong> while the match
          still appears in Riot&apos;s recent history.
        </li>
        <li style={liStyle}>
          Custom games and some modes may not appear in API history—if auto fails, use{' '}
          <strong>manual confirmation</strong> below.
        </li>
      </ol>
    );
  } else if (id === 4) {
    autoSteps = (
      <ol style={olStyle}>
        <li style={liStyle}>
          Both players: free account on <strong>Ballchasing</strong>, then <strong>upload the replay</strong> from{' '}
          <strong>PC</strong> (set visibility to public or unlisted). Epic names here must match the replay.
        </li>
        <li style={liStyle}>
          After uploads finish, one player taps <strong>“We&apos;ve played – find winner”</strong>.
        </li>
        <li style={liStyle}>
          Console-only or no replay upload? Auto won&apos;t run—use <strong>manual confirmation</strong> below.
        </li>
      </ol>
    );
  }

  if (!autoSteps) return null;

  const confirmBanner = confirmOnly ? (
    <div
      style={{
        marginBottom: 12,
        padding: '12px 14px',
        borderRadius: 8,
        border: dark ? '1px solid rgba(253, 224, 71, 0.4)' : '1px solid #fcd34d',
        background: dark ? 'rgba(113, 63, 18, 0.35)' : '#fffbeb',
        color: dark ? '#fef9c3' : '#78350f',
        fontSize: '0.88rem',
        lineHeight: 1.5,
      }}
      role="status"
    >
      <strong>Chosen path:</strong> this challenge settles by{' '}
      <strong>both players confirming in the app</strong> (no automatic game API payout). Follow Path B below after
      the match.
    </div>
  ) : null;

  return (
    <div style={box} className="winner-payout-guide">
      <h4 style={hStyle}>How the right winner gets paid</h4>
      {confirmBanner}
      {mode === 'create' && (
        <p style={{ margin: '0 0 10px 0', color: dark ? '#94a3b8' : '#475569' }}>
          Read this before you play so nobody is surprised after the match.
        </p>
      )}

      {confirmOnly ? (
        <>
          <p style={{ margin: '0 0 6px 0', fontWeight: 600, color: dark ? '#86efac' : '#166534' }}>
            Path B — {manualTitle}
          </p>
          {manualCore}
        </>
      ) : autoVerify === false ? (
        <p style={{ margin: '0 0 12px 0', color: dark ? '#94a3b8' : '#475569' }}>
          This challenge does not have both player IDs verified for automatic checks. Use{' '}
          <strong>Path B</strong> after the match so the real winner is paid.
        </p>
      ) : (
        <>
          <p style={{ margin: '0 0 6px 0', fontWeight: 600, color: dark ? '#a5b4fc' : '#3730a3' }}>
            Path A — Automatic (when IDs are verified)
          </p>
          <p style={{ margin: '0 0 8px 0', color: dark ? '#94a3b8' : '#475569' }}>{autoIntro}</p>
          {autoSteps}
        </>
      )}

      {!confirmOnly && (
        <>
          <p style={{ margin: '0 0 6px 0', fontWeight: 600, color: dark ? '#86efac' : '#166534' }}>
            Path B — {manualTitle}
          </p>
          {manualCore}
        </>
      )}

      {!compact && (
        <p style={{ margin: '12px 0 0 0', fontSize: '0.82rem', color: dark ? '#64748b' : '#64748b' }}>
          More context:{' '}
          <Link to="/about" style={{ color: dark ? '#93c5fd' : '#2563eb' }}>
            How EWIO works
          </Link>
          .
        </p>
      )}
    </div>
  );
}
